import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Calendar,
  CheckCircle2,
  KeyRound,
  Mail,
  Phone,
  Save,
  Settings,
  Shield,
  User,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/shared";
import { ChangePasswordDialog } from "@/components/account/ChangePasswordDialog";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useUpdateProfileMutation, useUserProfile } from "@/hooks/queries";
import { getApiErrorMessage } from "@/services/httpClient";
import { getInitials, splitName } from "@/lib/account-profile-utils";

const settingsLinks = [
  { to: "/admin/settings/platform", label: "Platform Settings", description: "Commission, payouts, BV pools" },
  { to: "/admin/settings/email", label: "Email Settings", description: "Outbound providers & limits" },
  { to: "/admin/settings/integrations", label: "Integrations", description: "Payment & third-party keys" },
  { to: "/admin/settings/levels", label: "Referral Levels", description: "Level commission structure" },
];

const AdminProfile = () => {
  const { user, refreshUser } = useAuth();
  const { toast } = useToast();
  const { data: profile, isLoading } = useUserProfile();
  const updateProfile = useUpdateProfileMutation();
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });

  useEffect(() => {
    if (!profile) return;
    setForm({
      name: [profile.firstName, profile.lastName].filter(Boolean).join(" "),
      email: profile.email,
      phone: profile.phoneNumber ?? "",
    });
  }, [profile]);

  const handleSaveProfile = async () => {
    if (!profile) return;
    try {
      const { firstName, lastName } = splitName(form.name);
      await updateProfile.mutateAsync({
        ...profile,
        firstName,
        lastName,
        email: form.email,
        phoneNumber: form.phone,
      });
      await refreshUser();
      toast({ title: "Profile saved", description: "Your personal information has been updated." });
    } catch (error) {
      toast({ title: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  if (isLoading) {
    return (
      <div className="mx-auto max-w-5xl space-y-6">
        <PageHeader title="My Profile" description="Manage your admin account and security." />
        <Skeleton className="h-36 rounded-2xl" />
        <div className="grid gap-6 lg:grid-cols-3">
          <Skeleton className="h-80 rounded-2xl lg:col-span-2" />
          <Skeleton className="h-80 rounded-2xl" />
        </div>
      </div>
    );
  }

  const displayName = form.name || user?.name || "Admin";
  const initials = getInitials(displayName);
  const memberSince = profile?.createdAt
    ? new Date(profile.createdAt).toLocaleDateString("en-IN", { month: "long", year: "numeric" })
    : "—";

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <PageHeader title="My Profile" description="Manage your admin account and security." />

      <div className="relative overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-accent/5" />
        <div className="relative flex flex-col gap-5 p-6 sm:flex-row sm:items-center sm:p-8">
          <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-2xl bg-gradient-primary text-2xl font-bold text-primary-foreground shadow-lg ring-4 ring-primary/10">
            {initials}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <p className="font-heading text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
                {displayName}
              </p>
              <Badge variant="secondary">Administrator</Badge>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">{form.email}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Badge variant="outline" className="gap-1">
                <Calendar className="h-3 w-3" />
                Since {memberSince}
              </Badge>
              {profile?.emailConfirmed && (
                <Badge variant="outline" className="gap-1 border-success/30 text-success">
                  <CheckCircle2 className="h-3 w-3" />
                  Email verified
                </Badge>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="border-border shadow-sm lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <User className="h-5 w-5 text-primary" />
              Personal information
            </CardTitle>
            <CardDescription>Update your name and contact details.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="admin-name">Full name</Label>
              <Input
                id="admin-name"
                className="h-11"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="admin-email" className="flex items-center gap-1.5">
                  <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                  Email
                </Label>
                <Input
                  id="admin-email"
                  type="email"
                  className="h-11"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-phone" className="flex items-center gap-1.5">
                  <Phone className="h-3.5 w-3.5 text-muted-foreground" />
                  Phone
                </Label>
                <Input
                  id="admin-phone"
                  type="tel"
                  className="h-11"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
              </div>
            </div>
            <div className="flex justify-end pt-1">
              <Button onClick={handleSaveProfile} disabled={updateProfile.isPending} className="gap-2">
                <Save className="h-4 w-4" />
                {updateProfile.isPending ? "Saving…" : "Save changes"}
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card className="border-border shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Shield className="h-5 w-5 text-primary" />
                Security
              </CardTitle>
              <CardDescription>Keep your admin account secure.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-xl border border-border bg-muted/30 p-4">
                <p className="text-sm font-medium text-foreground">Password</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Use a unique, strong password for this admin account.
                </p>
                <Button
                  variant="outline"
                  className="mt-3 w-full gap-2"
                  onClick={() => setPasswordOpen(true)}
                >
                  <KeyRound className="h-4 w-4" />
                  Change password
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Settings className="h-5 w-5 text-primary" />
                Settings shortcuts
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {settingsLinks.map((link) => (
                <Button
                  key={link.to}
                  variant="outline"
                  size="sm"
                  className="h-auto w-full justify-start px-3 py-2.5 text-left"
                  asChild
                >
                  <Link to={link.to}>
                    <div>
                      <p className="text-sm font-medium">{link.label}</p>
                      <p className="text-[11px] font-normal text-muted-foreground">{link.description}</p>
                    </div>
                  </Link>
                </Button>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>

      <ChangePasswordDialog open={passwordOpen} onOpenChange={setPasswordOpen} />
    </div>
  );
};

export default AdminProfile;
